import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import type { AuthResponse, User } from '@/types/api';

export function useAuthConfig() {
  return useQuery({
    queryKey: ['auth-config'],
    queryFn: async () => {
      const { data } = await api.get<{
        ms_sso_enabled: boolean;
        ms_client_id: string | null;
        ms_tenant_id: string | null;
      }>('/auth/config');
      return data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: async (vars: { email: string; password: string; remember_me?: boolean }) => {
      const { data } = await api.post<AuthResponse>('/auth/login', vars);
      return data;
    },
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout');
    },
  });
}

export function useCheckInvite(token: string) {
  return useQuery({
    queryKey: ['invite-check', token],
    queryFn: async () => {
      const { data } = await api.get<{
        email: string;
        org_name: string | null;
        user_exists: boolean;
        already_accepted: boolean;
        expired: boolean;
      }>(`/auth/invite/check?token=${encodeURIComponent(token)}`);
      return data;
    },
    enabled: !!token,
    retry: false,
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { token: string; password?: string; full_name?: string }) => {
      const { data } = await api.post<AuthResponse>('/auth/invite/accept', vars);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      await api.post('/auth/password-reset/request', vars);
    },
  });
}

export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: async (vars: { token: string; new_password: string }) => {
      await api.post('/auth/password-reset/confirm', vars);
    },
  });
}

export function useMfaValidate() {
  return useMutation({
    mutationFn: async (vars: { mfa_challenge_token: string; code: string }) => {
      const { data } = await api.post<AuthResponse>('/auth/mfa/validate', vars);
      return data;
    },
  });
}

export function useMfaStatus() {
  return useQuery({
    queryKey: ['mfa-status'],
    queryFn: async () => {
      const { data } = await api.get<{ enabled: boolean }>('/auth/mfa/status');
      return data;
    },
  });
}

export function useMfaSetup() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.get<{ secret: string; uri: string }>('/auth/mfa/setup');
      return data;
    },
  });
}

export function useMfaEnable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { code: string }) => {
      await api.post('/auth/mfa/enable', vars);
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['mfa-status'] });
      const { data } = await api.get<User>('/users/me');
      useAuthStore.getState().setUser(data);
    },
  });
}

export function useMfaDisable() {
  return useMutation({
    mutationFn: async (vars: { password: string }) => {
      await api.delete('/auth/mfa', { data: vars });
    },
  });
}
